// src/components/CompanionSheet.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  getCompanion,
  updateCompanion,
  markCompanionStress,
  clearCompanionStress,
  applyPendingUpgrade,
  COMPANION_UPGRADES
} from '../services/companionService';
import { IconChevronLeft, IconHeart, IconShield, IconZap, IconPlus, IconMinus } from './icons';
import CompanionLevelUpManager from './CompanionLevelUpManager';

const CompanionSheet = () => {
  const navigate = useNavigate();
  const { characterId: rangerId } = useParams();

  const [companion, setCompanion] = useState(null);
  const [ranger, setRanger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLevelUp, setShowLevelUp] = useState(false);

  // Load companion data
  useEffect(() => {
    loadCompanion();
  }, [rangerId]);

  const loadCompanion = async () => {
    if (!rangerId) return;

    setLoading(true);
    try {
      // Load companion
      const companionData = await getCompanion(rangerId);

      if (!companionData) {
        toast.error('Companion not found');
        navigate(`/character/${rangerId}`);
        return;
      }

      setCompanion(companionData);

      // Load ranger data for context
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase/config');
      const rangerRef = doc(db, 'characters', rangerId);
      const rangerSnap = await getDoc(rangerRef);

      if (rangerSnap.exists()) {
        setRanger({ id: rangerSnap.id, ...rangerSnap.data() });
      }

      // Show level-up UI if there are pending upgrades
      if (companionData.pendingUpgrades > 0) {
        setShowLevelUp(true);
      }
    } catch (error) {
      console.error('Error loading companion:', error);
      toast.error('Failed to load companion');
    } finally {
      setLoading(false);
    }
  };

  // Handle stress updates (similar to character sheet vitals)
  const updateStress = async (newStress) => {
    if (!companion) return;

    // Clamp between 0 and maxStress
    const clampedStress = Math.max(0, Math.min(newStress, companion.maxStress));

    try {
      await updateCompanion(rangerId, companion.id, { stress: clampedStress });
      await loadCompanion(); // Reload to get updated data
    } catch (error) {
      console.error('Error updating stress:', error);
    }
  };

  // Handle level-up complete
  const handleLevelUpComplete = async () => {
    setShowLevelUp(false);
    await loadCompanion(); // Reload companion data
  };

  // Trigger retroactive leveling manually (for companions created before this feature)
  const handleTriggerLevelUp = async () => {
    if (!companion || !ranger) return;

    const levelsToGain = ranger.level - companion.level;

    if (levelsToGain > 0) {
      try {
        const { autoLevelCompanion } = await import('../services/companionService');
        await autoLevelCompanion(rangerId, companion.id, ranger.level);
        await loadCompanion(); // This will trigger the level-up UI
      } catch (error) {
        console.error('Error triggering level-up:', error);
        toast.error('Failed to trigger level-up');
      }
    } else {
      toast('Companion is already at ranger level', { icon: '✓' });
    }
  };

  // Check if companion is dropped out (all stress marked)
  const isDroppedOut = companion && companion.stress >= companion.maxStress;

  // Get ranger abilities that affect companion
  const getRangerBonuses = () => {
    if (!companion) return [];

    const bonuses = [];

    companion.levelUpChoices?.forEach((choice) => {
      const upgrade = COMPANION_UPGRADES.find((u) => u.id === choice.upgrade);
      if (upgrade && upgrade.affectsRanger) {
        bonuses.push({
          name: upgrade.name,
          description: upgrade.description
        });
      }
    });

    return bonuses;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
        <p className="text-xl text-white">Loading companion...</p>
      </div>
    );
  }

  if (!companion) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
        <p className="text-xl text-white">Companion not found</p>
      </div>
    );
  }

  // Show level-up manager if there are pending upgrades
  if (showLevelUp && companion.pendingUpgrades > 0) {
    return (
      <CompanionLevelUpManager
        companion={companion}
        rangerId={rangerId}
        onComplete={handleLevelUpComplete}
      />
    );
  }

  return (
    <div className="p-2 sm:p-4 lg:p-6 max-w-7xl mx-auto bg-gradient-to-br from-gray-900 via-gray-800 to-black min-h-screen">
      {/* Back to Ranger Button */}
      <button
        onClick={() => navigate(`/character/${rangerId}`)}
        className="flex items-center gap-2 mb-4 px-4 py-2 bg-red-900 text-white rounded-md hover:bg-red-800 transition-colors border border-red-700 shadow-lg"
      >
        <IconChevronLeft />
        Back to {ranger?.name || 'Ranger'}
      </button>

      {/* Hero Section with Large Image */}
      <div className="mb-6 bg-gradient-to-r from-red-900 to-red-800 rounded-lg shadow-2xl border border-red-700 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
          {/* Image Section */}
          {companion.imageUrl && (
            <div className="relative h-64 lg:h-96">
              <img
                src={companion.imageUrl}
                alt={companion.name}
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            </div>
          )}

          {/* Info Section */}
          <div className={`p-6 flex flex-col justify-center ${!companion.imageUrl ? 'col-span-2' : ''}`}>
            <h1 className="text-4xl lg:text-5xl font-bold text-white drop-shadow-lg font-serif mb-2">
              {companion.name}
            </h1>
            <p className="text-2xl text-gray-200 mb-1">
              {companion.animalType} Companion
            </p>
            <p className="text-lg text-gray-300 mb-4">
              Level {companion.level} • {ranger?.name}'s Loyal Companion
            </p>

            {/* Attack Description */}
            {companion.attackDescription && (
              <div className="mt-2 p-3 bg-black/30 rounded-lg border border-red-600">
                <p className="text-sm text-gray-300 italic">
                  "{companion.attackDescription}"
                </p>
              </div>
            )}

            {/* Dropped Out Warning */}
            {isDroppedOut && (
              <div className="mt-4 p-4 bg-red-950 border border-red-600 rounded-lg">
                <p className="font-bold text-red-200">
                  ⚠️ {companion.name} has dropped out!
                </p>
                <p className="text-sm text-red-300">
                  They'll return after a long rest.
                </p>
              </div>
            )}

            {/* Level-Up Trigger (for companions behind in level or with no pending upgrades) */}
            {ranger && companion.level < ranger.level && (!companion.pendingUpgrades || companion.pendingUpgrades === 0) && (
              <div className="mt-4 p-4 bg-yellow-950 border border-yellow-600 rounded-lg">
                <p className="font-bold text-yellow-200 mb-2">
                  🎓 {companion.name} needs to level up!
                </p>
                <p className="text-sm text-yellow-300 mb-3">
                  Your companion is level {companion.level}, but you're level {ranger.level}. Click below to catch them up!
                </p>
                <button
                  onClick={handleTriggerLevelUp}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors font-bold"
                >
                  Level Up to {ranger.level} ({ranger.level - companion.level} upgrades)
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Single Consolidated Panel */}
      <div className="bg-gray-900 rounded-lg p-6 shadow-2xl border border-red-700">
        {/* Combat Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 pb-6 border-b border-red-700">
          {/* Evasion */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <IconShield className="text-red-500" />
              <h3 className="text-lg font-bold text-white">Evasion</h3>
            </div>
            <div className="text-4xl font-bold text-red-400">
              {companion.evasion}
            </div>
          </div>

          {/* Stress Tracker */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <IconZap className="text-yellow-400" />
              <h3 className="text-lg font-bold text-white">Stress</h3>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <button
                onClick={() => updateStress((companion.stress || 0) - 1)}
                disabled={companion.stress === 0}
                className="p-1 rounded-full bg-gray-800 text-gray-300 hover:bg-red-900 hover:text-white border border-gray-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <IconMinus/>
              </button>
              <div className="flex space-x-1">
                {Array.from({ length: companion.maxStress }, (_, i) => (
                  <div
                    key={i}
                    className={`w-4 h-4 border-2 border-yellow-600 rounded-sm shadow-sm ${
                      i < (companion.stress || 0) ? 'bg-yellow-600 shadow-yellow-600/50' : 'bg-gray-800 border-yellow-800'
                    }`}
                    title={`Stress ${i + 1}/${companion.maxStress}`}
                  />
                ))}
              </div>
              <button
                onClick={() => updateStress((companion.stress || 0) + 1)}
                disabled={isDroppedOut}
                className="p-1 rounded-full bg-gray-800 text-gray-300 hover:bg-red-900 hover:text-white border border-gray-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <IconPlus/>
              </button>
            </div>
          </div>

          {/* Attack */}
          <div className="text-center">
            <h3 className="text-lg font-bold text-white mb-2">Attack</h3>
            <div className="flex items-center justify-center gap-3">
              <div>
                <span className="text-xs text-gray-400 block">Damage</span>
                <span className="text-2xl font-bold text-red-400">{companion.damageDie}</span>
              </div>
              <div className="text-gray-600">•</div>
              <div>
                <span className="text-xs text-gray-400 block">Range</span>
                <span className="text-2xl font-bold text-red-400">{companion.damageRange}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Attack Description */}
        <div className="mb-6 pb-6 border-b border-red-700">
          <p className="text-gray-300 italic">"{companion.attackDescription}"</p>
          {ranger && (
            <p className="text-sm text-red-300 mt-2">
              Uses <strong>{ranger.name}'s Proficiency</strong> modifier
            </p>
          )}
        </div>

        {/* Experiences */}
        <div className="mb-6 pb-6 border-b border-red-700">
          <h3 className="text-lg font-bold text-white mb-3">Experiences</h3>
          <div className="flex flex-wrap gap-2">
            {companion.experiences.map((exp, idx) => (
              <div
                key={idx}
                className="inline-flex items-center gap-2 px-3 py-2 bg-gray-800 rounded-lg border border-red-700"
              >
                <span className="font-medium text-gray-200">{exp.name}</span>
                <span className="px-2 py-0.5 bg-red-600 text-white rounded-full text-xs font-bold">
                  +{exp.bonus}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Abilities */}
        {companion.levelUpChoices && companion.levelUpChoices.length > 0 && (
          <div className="mb-6 pb-6 border-b border-red-700">
            <h3 className="text-lg font-bold text-white mb-3">Abilities</h3>
            <div className="space-y-2">
              {companion.levelUpChoices.map((choice, idx) => {
                const upgrade = COMPANION_UPGRADES.find((u) => u.id === choice.upgrade);
                return (
                  <div key={idx} className="flex items-start gap-2">
                    <span className="px-2 py-1 bg-red-800 text-white text-xs rounded font-bold shrink-0">
                      L{choice.level}
                    </span>
                    <div className="flex-1">
                      <span className="font-bold text-gray-200">{upgrade?.name || choice.upgrade}</span>
                      <span className="text-sm text-gray-400 ml-2">{upgrade?.description}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Ranger Bonuses */}
        {getRangerBonuses().length > 0 && (
          <div className="mb-6 pb-6 border-b border-red-700">
            <h3 className="text-lg font-bold text-white mb-2">Ranger Bonuses</h3>
            <div className="space-y-2">
              {getRangerBonuses().map((bonus, idx) => (
                <div key={idx} className="p-2 bg-green-950 rounded-lg border border-green-700">
                  <span className="font-bold text-green-300">{bonus.name}</span>
                  <span className="text-sm text-green-400 ml-2">{bonus.description}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Special Mechanic: Armored */}
        {companion.levelUpChoices?.some(choice => choice.upgrade === 'armored') && (
          <div className="mb-6 pb-6 border-b border-red-700">
            <div className="p-4 bg-blue-950 rounded-lg border-2 border-blue-600">
              <h3 className="text-lg font-bold text-blue-200 mb-2">🛡️ Armored Protection</h3>
              <p className="text-sm text-blue-300">
                When {companion.name} would take damage and mark Stress, {ranger?.name || 'the ranger'} can choose to mark an Armor slot instead.
              </p>
              <p className="text-xs text-blue-400 mt-2 italic">
                Note: The GM should ask "{ranger?.name || 'the ranger'}, do you want to mark an Armor slot to protect {companion.name}?" when the companion takes damage.
              </p>
            </div>
          </div>
        )}

        {/* Command Guide */}
        <div>
          <h3 className="text-lg font-bold text-white mb-3">How to Command</h3>
          <div className="text-sm text-gray-300 space-y-1">
            <p>• <strong className="text-white">Action:</strong> Use your action to command {companion.name}</p>
            <p>• <strong className="text-white">Roll:</strong> Make a Spellcast roll</p>
            <p>• <strong className="text-white">Hope:</strong> Spend Hope to add an Experience bonus</p>
            <p>• <strong className="text-white">Attack:</strong> On success, {companion.name} attacks using your Proficiency</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanionSheet;
